import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  normalizeFileUrl,
  urlToPrintableSrc,
} from "../../../util/printableImage";

const BodyMapView = ({ bodyMaps }) => {
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const maps = useMemo(() => {
    const source = bodyMaps ?? reqDet?.bodymaps;
    return Array.isArray(source) ? source.filter(Boolean) : [];
  }, [bodyMaps, reqDet?.bodymaps]);

  const [printableSrcByUrl, setPrintableSrcByUrl] = useState({});

  const imageUrls = useMemo(() => {
    try {
      const urls = maps
        ?.map((frm) => normalizeFileUrl(frm?.file))
        ?.filter(Boolean);
      return [...new Set(urls)];
    } catch {
      return [];
    }
  }, [maps]);

  const imageUrlsKey = useMemo(() => JSON.stringify(imageUrls), [imageUrls]);

  useEffect(() => {
    let cancelled = false;
    let urls = [];
    try {
      urls = JSON.parse(imageUrlsKey);
      if (!Array.isArray(urls)) urls = [];
    } catch {
      urls = [];
    }

    const loadImages = async () => {
      try {
        const settled = await Promise.allSettled(
          urls.map(async (url) => {
            try {
              const printable = await urlToPrintableSrc(url);
              return [url, printable || url];
            } catch {
              return [url, url];
            }
          }),
        );
        if (cancelled) return;

        setPrintableSrcByUrl((prev) => {
          const next = { ...(prev ?? {}) };
          settled?.forEach((result) => {
            if (result?.status !== "fulfilled" || !result?.value) return;
            const [url, printable] = result.value;
            if (url && printable) next[url] = printable;
          });
          return next;
        });
      } catch {
        // Never let image preload crash the screen.
      }
    };

    if (urls.length > 0) loadImages();

    return () => {
      cancelled = true;
    };
  }, [imageUrlsKey]);

  if (!maps.length) return null;

  return (
    <div className="ph20 body-map-view-root">
      {maps.map((frm, i) => {
        try {
          const fileUrl = normalizeFileUrl(frm?.file);
          if (!fileUrl) return null;
          const imageSrc = printableSrcByUrl?.[fileUrl] || fileUrl;

          return (
            <div
              className="mt5 flex-row-ali-cen body-map-item"
              key={frm?.mapId ?? `body-map-${i}`}
            >
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor body-map-link"
              >
                <img
                  src={imageSrc}
                  className="br5 file-view-image body-map-image"
                  alt={frm?.name ?? "body map"}
                  loading="eager"
                  decoding="async"
                  onError={(e) => {
                    try {
                      if (
                        e?.currentTarget &&
                        fileUrl &&
                        e.currentTarget.src !== fileUrl
                      ) {
                        e.currentTarget.src = fileUrl;
                      }
                    } catch {
                      // ignore img fallback errors
                    }
                  }}
                />
              </a>
            </div>
          );
        } catch {
          return null;
        }
      })}
    </div>
  );
};

export default BodyMapView;
